import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  UnauthorizedError,
  deleteArtwork,
  getArtworkStats,
  listArtworksPage,
  thumbUrl,
  updateArtwork,
  uploadArtworks,
} from "./artGalleryApi";
import { STATUSES, STATUS_LABEL, TYPES, TYPE_LABEL, formatPrice, formatSize, hasSize } from "./artGalleryShared";
import ArtworkEditor from "./ArtworkEditor";
import ScaleLineup from "./ScaleLineup";

const PAGE_SIZE = 48;

const WEBSITE_ONLY_FILTERS = ["draft", "noartist"];
const QUICK_FILTERS = [
  { id: "all", label: "All" },
  ...STATUSES,
  { id: "draft", label: "Drafts" },
  { id: "review", label: "Needs review" },
  { id: "nosize", label: "No size" },
  { id: "noartist", label: "No artist" },
];

const SORTS = [
  { id: "order", label: "Collection order", section: "presentation" },
  { id: "newest", label: "Newest first" },
  { id: "oldest", label: "Oldest first" },
  { id: "title", label: "Title A–Z" },
  { id: "price-desc", label: "Price high–low" },
  { id: "price-asc", label: "Price low–high" },
];

const quickFilterParams = (id) => {
  if (STATUSES.some((s) => s.id === id)) return { status: id };
  if (id === "draft") return { published: "false" };
  if (id === "review") return { review: "true" };
  if (id === "nosize") return { review: "nosize" };
  if (id === "noartist") return { artist: "none" };
  return {};
};

const UploadZone = ({ section, onUploaded, onUnauthorized }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type));
    if (!files.length) {
      toast.error("Choose JPEG, PNG or WebP images");
      return;
    }
    setProgress(0);
    try {
      const result = await uploadArtworks(files, setProgress, section);
      onUploaded(result.artworks);
      const count = result.artworks.length;
      const added = `Added ${count} artwork${count === 1 ? "" : "s"}${section === "website" ? " as drafts" : ""}`;
      toast.success(`${added} - check the crops and details`);
      if (result.failed?.length) toast.error(`Could not process: ${result.failed.join(", ")}`);
    } catch (error) {
      if (error instanceof UnauthorizedError) onUnauthorized();
      toast.error(error.message);
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div
      className={`ag-upload ${dragging ? "is-dragging" : ""}`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        upload(e.dataTransfer.files);
      }}
      onClick={() => progress === null && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => upload(e.target.files)}
      />
      {progress === null ? (
        <>
          <strong>Drop photos of artwork here</strong>
          <span>or click to choose. Frames and walls are cropped out automatically.</span>
        </>
      ) : (
        <>
          <strong>{progress < 1 ? "Uploading..." : "Detecting artwork and cropping..."}</strong>
          <div className="ag-progress">
            <div style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        </>
      )}
    </div>
  );
};

const ArtworkCard = ({ artwork, isWebsite, onOpen, onStatusChange, onDelete }) => (
  <div className="ag-card" onClick={() => onOpen(artwork)}>
    <div className={`ag-card-image is-${artwork.presentation || "shadow"}`}>
      <img src={thumbUrl(artwork.croppedImageUrl, 480)} alt={artwork.title || "Untitled"} loading="lazy" />
      <div className="ag-card-badges">
        <span className={`ag-badge ag-badge-${artwork.status}`}>{STATUS_LABEL[artwork.status]}</span>
        {isWebsite && !artwork.published && <span className="ag-badge ag-badge-draft">Draft</span>}
        {artwork.needsReview && <span className="ag-badge ag-badge-review">Check crop</span>}
      </div>
      <button
        type="button"
        className="ag-card-delete"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(artwork);
        }}
        aria-label="Delete artwork"
        title="Delete artwork"
      >
        ✕
      </button>
    </div>
    <div className="ag-card-body">
      <div className="ag-card-title">{artwork.title || <em>Untitled</em>}</div>
      {isWebsite && (
        <div className="ag-card-meta">
          {artwork.artist?.name || <span className="ag-warn">No artist</span>}
          <span> · {TYPE_LABEL[artwork.type] || "Original"}</span>
        </div>
      )}
      <div className="ag-card-meta">
        {hasSize(artwork) ? formatSize(artwork) : <span className="ag-muted">No size</span>}
        {artwork.medium && <span> · {artwork.medium}</span>}
      </div>
      <div className="ag-card-footer" onClick={(e) => e.stopPropagation()}>
        <span className="ag-card-price">{formatPrice(artwork)}</span>
        <select value={artwork.status} onChange={(e) => onStatusChange(artwork, e.target.value)} aria-label="Status">
          {STATUSES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const ArtworksPanel = ({ section = "website", artists = [], subjects = [], onError, onUnauthorized }) => {
  const isWebsite = section === "website";
  const quickFilters = isWebsite ? QUICK_FILTERS : QUICK_FILTERS.filter((f) => !WEBSITE_ONLY_FILTERS.includes(f.id));
  const sorts = SORTS.filter((s) => !s.section || s.section === section);
  const [result, setResult] = useState({ artworks: [], total: 0, pages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quick, setQuick] = useState("all");
  const [artist, setArtist] = useState("");
  const [type, setType] = useState("");
  const [sort, setSort] = useState(sorts[0].id);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [view, setView] = useState("collection");
  const [editingId, setEditingId] = useState(null);
  const q = useDebounced(search);

  const params = {
    section,
    ...quickFilterParams(quick),
    ...(artist ? { artist } : {}),
    ...(type ? { type } : {}),
    sort,
    q,
    page,
    limit: PAGE_SIZE,
  };
  const paramsKey = JSON.stringify(params);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, nextStats] = await Promise.all([listArtworksPage(JSON.parse(paramsKey)), getArtworkStats(section)]);
      setResult(data);
      setStats(nextStats);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  }, [paramsKey, section, onError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => setPage(1), [q]);
  const resetting = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const replaceArtworks = useCallback((updated) => {
    setResult((current) => {
      const byId = new Map(updated.map((a) => [a._id, a]));
      const merged = current.artworks.map((a) => byId.get(a._id) || a);
      const added = updated.filter((a) => !current.artworks.some((c) => c._id === a._id));
      return { ...current, artworks: [...added, ...merged], total: current.total + added.length };
    });
    getArtworkStats(section).then(setStats).catch(() => {});
  }, [section]);

  const removeArtwork = useCallback(
    (id) => {
      setResult((current) => ({ ...current, artworks: current.artworks.filter((a) => a._id !== id), total: current.total - 1 }));
      setEditingId((current) => (current === id ? null : current));
      getArtworkStats(section).then(setStats).catch(() => {});
    },
    [section]
  );

  const changeStatus = async (artwork, status) => {
    try {
      replaceArtworks([await updateArtwork(artwork._id, { status })]);
    } catch (error) {
      onError(error);
    }
  };

  const confirmDelete = async (artwork) => {
    const live = artwork.published ? " It will be removed from the website." : "";
    if (!window.confirm(`Delete "${artwork.title || "this artwork"}"?${live} This can't be undone.`)) return;
    try {
      await deleteArtwork(artwork._id);
      removeArtwork(artwork._id);
      toast.success("Artwork deleted");
    } catch (error) {
      onError(error);
    }
  };

  const editing = result.artworks.find((a) => a._id === editingId) || null;
  const quickCount = (id) => {
    if (!stats) return null;
    if (id === "all") return stats.total;
    if (id === "draft") return stats.unpublished;
    if (id === "review") return stats.review;
    if (id === "noartist") return stats.noArtist;
    if (id === "nosize") return stats.noSize ?? null;
    return stats[id] ?? 0;
  };

  return (
    <>
      <div className="ag-toolbar">
        <input
          className="ag-search"
          type="search"
          placeholder={isWebsite ? "Search title, artist or medium" : "Search title or medium"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isWebsite && (
          <>
            <select value={artist} onChange={(e) => resetting(setArtist)(e.target.value)} aria-label="Artist">
              <option value="">All artists</option>
              {artists.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.name} ({a.artworkCount})
                </option>
              ))}
            </select>
            <select value={type} onChange={(e) => resetting(setType)(e.target.value)} aria-label="Type">
              <option value="">All types</option>
              {TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </>
        )}
        <select value={sort} onChange={(e) => resetting(setSort)(e.target.value)} aria-label="Sort">
          {sorts.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="ag-segmented">
          <button className={view === "collection" ? "is-active" : ""} onClick={() => setView("collection")}>
            Collection
          </button>
          <button className={view === "lineup" ? "is-active" : ""} onClick={() => setView("lineup")}>
            Scale lineup
          </button>
        </div>
      </div>

      <div className="ag-filters">
        {quickFilters.map((f) => (
          <button key={f.id} className={`ag-chip ${quick === f.id ? "is-active" : ""}`} onClick={() => resetting(setQuick)(f.id)}>
            {f.label} {quickCount(f.id) !== null && <span>{quickCount(f.id)}</span>}
          </button>
        ))}
      </div>

      {view === "collection" ? (
        <>
          <UploadZone section={section} onUploaded={replaceArtworks} onUnauthorized={onUnauthorized} />
          {loading && !result.artworks.length ? (
            <div className="ag-empty">Loading collection...</div>
          ) : result.artworks.length ? (
            <div className={`ag-grid ${loading ? "is-loading" : ""}`}>
              {result.artworks.map((artwork) => (
                <ArtworkCard
                  key={artwork._id}
                  artwork={artwork}
                  isWebsite={isWebsite}
                  onOpen={(a) => setEditingId(a._id)}
                  onStatusChange={changeStatus}
                  onDelete={confirmDelete}
                />
              ))}
            </div>
          ) : (
            <div className="ag-empty">No artworks match these filters.</div>
          )}
        </>
      ) : (
        <ScaleLineup artworks={result.artworks} onOpen={(a) => setEditingId(a._id)} />
      )}

      {result.pages > 1 && (
        <div className="ag-pager">
          <button className="ag-btn ag-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            ← Previous
          </button>
          <span>
            Page {page} of {result.pages} · {result.total} artworks
          </span>
          <button className="ag-btn ag-btn-ghost" disabled={page >= result.pages} onClick={() => setPage((p) => p + 1)}>
            Next →
          </button>
        </div>
      )}

      {editing && (
        <ArtworkEditor
          key={editing._id}
          artwork={editing}
          section={section}
          artists={artists}
          subjects={subjects}
          onClose={() => setEditingId(null)}
          onSaved={replaceArtworks}
          onDeleted={removeArtwork}
          onError={onError}
        />
      )}
    </>
  );
};

export default ArtworksPanel;
